import React from 'react';
import styled from 'styled-components';
import NavBar from './nav_bar';
import {
  MainHeader,
  Body,
  SecondaryBody,
  SectionTitle,
  ExternalLink,
} from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { withLocalePrefix } from 'shared/utils/translation';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Row } from '@grnhse/seedling/lib/birch/layouts/row';
import FullWidth from '@grnhse/seedling/lib/birch/layouts/full_width';
import { WarningCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import PreviewIcon from '@grnhse/seedling/lib/birch/icons/preview';

const t = withLocalePrefix('developers_greenhouse_root');

const HARVEST_V3_MIGRATION_GUIDE_URL =
  'https://docs.google.com/presentation/d/1IKEL-YfSzlAgaPju3VsozEthhbbT_LXgzeG-DYLTnOc/edit?usp=sharing';

type ResourceCard = {
  id: string;
  category: 'recruiting' | 'onboarding';
  docUrl?: string;
};

const RESOURCE_CARDS: ResourceCard[][] = [
  [
    {
      id: 'assessment_api',
      category: 'recruiting',
      docUrl: 'https://docs.greenhouse.io/assessment.html',
    },
    {
      id: 'audit_log_api',
      category: 'recruiting',
      docUrl: 'https://docs.greenhouse.io/audit-log.html',
    },
    {
      id: 'harvest_v3_api',
      category: 'recruiting',
      docUrl: 'https://harvestdocs.greenhouse.io/',
    },
  ],
  [
    {
      id: 'harvest_v1_v2_api',
      category: 'recruiting',
      docUrl: 'https://docs.greenhouse.io/harvest.html',
    },
    {
      id: 'ingestion_api',
      category: 'recruiting',
      docUrl: 'https://docs.greenhouse.io/candidate-ingestion.html',
    },
    {
      id: 'job_board_api',
      category: 'recruiting',
      docUrl: 'https://docs.greenhouse.io/job-board.html',
    },
  ],
  [
    {
      id: 'recruiting_webhooks',
      category: 'recruiting',
      docUrl: 'https://docs.greenhouse.io/webhooks.html',
    },
    {
      id: 'graphql',
      category: 'onboarding',
      docUrl: 'https://docs.greenhouse.io/gho.html',
    },
    {
      id: 'onboarding_webhooks',
      category: 'onboarding',
      docUrl: 'https://docs.greenhouse.io/onboarding_webhooks.html',
    },
  ],
  [
    {
      id: 'rest_api',
      category: 'onboarding',
      docUrl: 'https://onboarding-api.greenhouse.io/api-docs/index.html',
    },
  ],
];

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f9faf9;
  font-family: 'Untitled Sans', sans-serif;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 48px 24px 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentArea = styled.div`
  width: 100%;
  max-width: 1488px;
`;

const PageTitle = styled(MainHeader)`
  color: #424242;
  margin-bottom: 24px;
`;

const SubtitleRow = styled(Row).attrs(() => ({ spacing: 2 }))`
  margin-bottom: 24px;
`;

const WarningCallOutWrapper = styled.div`
  margin-bottom: 24px;
`;

const CardWrapper = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid #ccd4d1;
  border-radius: 8px;
  box-shadow: 0px 2px 3px rgba(21, 55, 44, 0.13);
  background: #ffffff;
  min-height: 160px;

  &:hover {
    background: #ecf3ff;
  }
`;

const CardCategory = styled(SecondaryBody)`
  color: ${FullPalette.EVERGREEN_E400};
`;

const CardTitle = styled(SectionTitle)`
  color: ${FullPalette.EVERGREEN_700};
`;

const CardDescription = styled(SecondaryBody)`
  color: ${FullPalette.EVERGREEN_700};
`;

const FooterBar = styled.footer`
  height: 84px;
  background: ${FullPalette.EVERGREEN_700};
  display: flex;
  align-items: center;
  padding: 0 200px;
`;

const FooterText = styled(SecondaryBody)`
  color: white;
  font-size: 12px;
`;

type CardProps = ResourceCard;

const ResourceCardItem = ({ id, category, docUrl }: CardProps) => (
  <CardWrapper>
    <CardCategory>{t(`categories.${category}`)}</CardCategory>
    <CardTitle>{t(`cards.${id}.title`)}</CardTitle>
    <CardDescription>{t(`cards.${id}.description`)}</CardDescription>
    {docUrl && (
      <ExternalLink href={docUrl}>{t('view_documentation')}</ExternalLink>
    )}
  </CardWrapper>
);

type Props = {
  signInPath: string;
  dashboardPath: string;
  signedIn: boolean;
};

const DevelopersGreenhouseRoot = ({
  signInPath,
  dashboardPath,
  signedIn,
}: Props) => (
  <PageWrapper>
    <NavBar
      signInPath={signInPath}
      dashboardPath={dashboardPath}
      signedIn={signedIn}
    />

    <MainContent>
      <ContentArea>
        <PageTitle>{t('page_title')}</PageTitle>
        <SubtitleRow>
          <Body>{t('subtitle')}</Body>
          <ExternalLink href="https://github.com/grnhse/greenhouse-api-docs">
            {t('view_on_github')}
          </ExternalLink>
        </SubtitleRow>

        <WarningCallOutWrapper>
          <WarningCallOut
            headerText={t('harvest_deprecation_banner.header')}
            bodyText={t('harvest_deprecation_banner.body')}
            buttonProps={{
              children: t('harvest_deprecation_banner.cta'),
              buttonType: 'secondary',
              size: 'lg',
              icon: PreviewIcon,
              iconPosition: 'right',
              onClick: () =>
                window.open(
                  HARVEST_V3_MIGRATION_GUIDE_URL,
                  '_blank',
                  'noopener,noreferrer'
                ),
            }}
          />
        </WarningCallOutWrapper>

        <Stack spacing={3}>
          {RESOURCE_CARDS.map((row, rowIdx) => (
            <FullWidth key={rowIdx}>
              <Grid container spacing={6}>
                {row.map((card) => (
                  <Grid xs={12} md={4} key={card.id}>
                    <ResourceCardItem {...card} />
                  </Grid>
                ))}
              </Grid>
            </FullWidth>
          ))}
        </Stack>
      </ContentArea>
    </MainContent>

    <FooterBar>
      <FooterText>{t('footer')}</FooterText>
    </FooterBar>
  </PageWrapper>
);

export default DevelopersGreenhouseRoot;
